// =============================================================================
// AmpFunding — third revision of the AMP promotional page.
// =============================================================================
// Replaces AmpProgram.tsx. Two polish changes from the prior revision:
//   - Hero CTA button removed; replaced with a non-clickable status line
//     reading "Virtual AMP Informational Workshop dates to be announced soon."
//     The interest form below the page remains the active sign-up path.
//   - Form submit button text now intentionally breaks between "the" and
//     "workshop list," with both lines centered, so the button reads as two
//     tight balanced lines.
//
// Prior content stays: the umbrella tagline, the three funding
// streams (now including Field Border, Filter Strip, Vegetative Barrier in
// the conservation examples), the workshop status block describing the
// one-hour virtual format, the hero produce image, etc.

import { useEffect, useState } from 'react';
import {
  Mail, ArrowLeft, Sprout, Tractor, ClipboardList,
  CheckCircle2, Users, AlertCircle, Video, Calendar,
} from 'lucide-react';
import { api, type PublicEvent } from '../api';

// AMP Recruitment events appear at the top of the hero. Filtered by the
// "AMP Recruitment" tag (seeded in migration 025). Future events only.
function AmpRecruitmentEvents() {
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.events({ includePast: false })
      .then((d) => {
        const filtered = (d.events ?? []).filter((e) =>
          (e.tags ?? []).some((t) => t.toLowerCase() === 'amp recruitment')
        );
        setEvents(filtered);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  if (events.length === 0) {
    return (
      <p className="text-base md:text-lg text-white font-semibold italic">
        Virtual AMP Informational Workshop dates to be announced soon.
      </p>
    );
  }

  return (
    <div className="mt-3 mb-2 inline-block bg-uif-brown-dark border-2 border-uif-brown-light rounded-lg shadow-xl px-6 py-4 text-left max-w-lg mx-auto ring-4 ring-white/30">
      <div className="text-xs uppercase tracking-widest text-white mb-2 font-bold">
        Upcoming AMP Informational Workshops
      </div>
      <ul className="space-y-2">
        {events.map((e) => {
          const start = new Date(e.start_date);
          const dateStr = start.toLocaleDateString('en-US', {
            weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
          });
          const timeStr = start.toLocaleTimeString('en-US', {
            hour: 'numeric', minute: '2-digit', timeZone: 'UTC',
          });
          return (
            <li key={e.event_id}>
              <a
                href={`/#event/${e.event_id}`}
                className="block text-white hover:text-uif-brown-light underline decoration-white/60 hover:decoration-white"
              >
                <span className="block font-bold text-base">{e.title}</span>
                <span className="block text-white/90 text-sm pl-4">{dateStr} · {timeStr}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Static content
// ---------------------------------------------------------------------------

const INTRO_PARAGRAPHS = [
  "AMP is USDA's Advancing Markets for Producers program — operated locally by Up in Farms LLC for smaller fruit, vegetable, and livestock farms across Mississippi. The program pairs federal funding with hands-on, season-by-season support so small operations can build the systems that buyers, food hubs, schools, and institutions can actually count on — while protecting and improving the soil at the same time.",
  "AMP is competitive and slots are limited. If you're interested, the next step is attending one of our upcoming AMP Informational Workshops — please reach out as soon as you can so we can save you a seat.",
];

const UMBRELLA_TAGLINE =
  "Our AMP project combines all of your plans for conservation, production, food safety, and marketing under a single umbrella to help you manage your farm activities in a unified way.";
const UMBRELLA_KICKER = "Plan now… grow at your own pace!";

interface FundingStream {
  icon: React.ReactNode;
  number: string;
  heading: string;
  body: string;
  examples?: string;
}

const FUNDING_STREAMS: FundingStream[] = [
  {
    icon: <Sprout className="h-7 w-7 text-uif-green" />,
    number: '1',
    heading: 'NRCS-approved conservation practices',
    body:
      "We hold a project-specific budget to support producers who implement officially recognized NRCS conservation practices on their farms. Up in Farms coordinates the technical paperwork — including the environmental review and sub-agreement — so you can focus on the practice itself.",
    examples:
      "Examples: cover cropping, conservation crop rotation, no-till or reduced tillage, prescribed grazing, nutrient management, mulching, conservation cover, stripcropping, field border, filter strip, vegetative barrier, and other NRCS-approved practices appropriate to your operation.",
  },
  {
    icon: <ClipboardList className="h-7 w-7 text-uif-green" />,
    number: '2',
    heading: 'Plans and implementation across three farm-business areas',
    body:
      "Beyond conservation, we have a project budget to assist producers with developing and implementing real, working plans in three of the highest-impact areas of a small farm business: Production, Sales & Marketing, and Food Safety. The funding is structured around the planning and the follow-through — both halves count.",
    examples:
      "Examples: production planning for a diversified crop or livestock rotation; sales and marketing strategy for direct-to-consumer (CSA, farmers markets, on-farm), wholesale, or institutional channels; food safety planning and implementation sized to your scale.",
  },
  {
    icon: <Tractor className="h-7 w-7 text-uif-green" />,
    number: '3',
    heading: 'Equipment and supplies under $10,000',
    body:
      "We have additional project funds available to assist with equipment and supply purchases for items priced under $10,000. This category is intentionally sized for small farm operations — right-sized tools that meaningfully improve efficiency without locking you into industrial-scale capital investment.",
    examples:
      "Examples: walk-behind tractor implements, post-harvest tools, cold storage components, pack/cool trailer pieces, traceability supplies, irrigation upgrades, and similar items aligned with your plan.",
  },
];

const AUDIENCE_POINTS: string[] = [
  "Smaller fruit and vegetable operations in Mississippi.",
  "Smaller livestock operations in Mississippi.",
  "At least two years of agricultural production experience.",
  "At least one agricultural product you grow or raise for market — not for personal use only.",
  "Willingness to participate in seasonal planning and verification (we'll walk you through it).",
];

const COMMITMENTS: string[] = [
  "You produce at least one agricultural product intended for market — crop, livestock, or value-added.",
  "You have at least two years of agricultural production history.",
  "You complete a soil test each season (we'll point you to a USDA-accepted lab).",
  "You provide your FSA Farm, Tract, and Field numbers. If you aren't set up with FSA yet, we'll help you get there.",
  "You keep basic records you can verify each season — paper logs and spreadsheets are accepted while our Farm2Cloud platform is being finalized.",
  "You engage honestly with the seasonal verification process. Communication beats perfection.",
];

// ---------------------------------------------------------------------------
// Interest form (mailto fallback)
// ---------------------------------------------------------------------------

const MARKET_CHANNELS = [
  'Wholesale / distributor',
  'Institutional (schools, hospitals)',
  'CSA / produce subscription',
  'Farmers market',
  'On-farm sales / farm stand',
  'Online or direct-ship',
  'Other',
];

interface FormState {
  full_name: string;
  farm_name: string;
  email: string;
  phone: string;
  county: string;
  what_you_grow: string;
  primary_markets: string[];
  message: string;
}

const EMPTY_FORM: FormState = {
  full_name: '', farm_name: '', email: '', phone: '', county: '',
  what_you_grow: '', primary_markets: [], message: '',
};

function InterestForm() {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);

  const update = <K extends keyof FormState>(k: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [k]: e.target.value }));
    };

  const toggleMarket = (channel: string) => {
    setForm((prev) => ({
      ...prev,
      primary_markets: prev.primary_markets.includes(channel)
        ? prev.primary_markets.filter((c) => c !== channel)
        : [...prev.primary_markets, channel],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const lines = [
      `Name: ${form.full_name}`,
      `Farm: ${form.farm_name}`,
      `Email: ${form.email}`,
      form.phone ? `Phone: ${form.phone}` : null,
      form.county ? `County: ${form.county}` : null,
      form.what_you_grow ? `\nWhat I grow/raise:\n${form.what_you_grow}` : null,
      form.primary_markets.length ? `\nPrimary markets: ${form.primary_markets.join(', ')}` : null,
      form.message ? `\nQuestions / notes:\n${form.message}` : null,
      `\nRequest: Please add me to the next AMP Informational Workshop.`,
    ].filter(Boolean).join('\n');

    const subject = `AMP Workshop Interest — ${form.farm_name || form.full_name || 'New producer'}`;
    const url = `mailto:support@upinfarms.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines)}`;
    window.location.href = url;
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="rounded-lg border border-uif-green/30 bg-uif-green/5 p-6 text-center space-y-3">
        <CheckCircle2 className="h-12 w-12 text-uif-green mx-auto" />
        <h3 className="text-xl font-bold text-uif-green-dark">Thanks — we'll be in touch</h3>
        <p className="text-gray-700">
          Your email client should have just opened with your inquiry pre-filled. Once you hit send,
          we'll receive it at <span className="font-mono text-sm">support@upinfarms.com</span> and
          reach back out about the next AMP Informational Workshop.
        </p>
        <p className="text-sm text-gray-600">
          If your email client didn't open, send the same details directly to{' '}
          <a href="mailto:support@upinfarms.com" className="text-uif-green font-semibold hover:underline">
            support@upinfarms.com
          </a>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Your name *">
          <input type="text" required value={form.full_name} onChange={update('full_name')}
            autoComplete="name" className={inputCls} />
        </Field>
        <Field label="Farm name">
          <input type="text" value={form.farm_name} onChange={update('farm_name')} className={inputCls} />
        </Field>
        <Field label="Email *">
          <input type="email" required value={form.email} onChange={update('email')}
            autoComplete="email" className={inputCls} />
        </Field>
        <Field label="Phone">
          <input type="tel" value={form.phone} onChange={update('phone')}
            autoComplete="tel" placeholder="(optional)" className={inputCls} />
        </Field>
        <Field label="Mississippi county">
          <input type="text" value={form.county} onChange={update('county')}
            placeholder="e.g. Hinds, Madison, Rankin" className={inputCls} />
        </Field>
        <div />
      </div>

      <Field label="What you grow or raise">
        <textarea value={form.what_you_grow} onChange={update('what_you_grow')} rows={2}
          placeholder="e.g. Mixed vegetables on 4 acres; pastured poultry; a small berry patch"
          className={inputCls} />
      </Field>

      <fieldset>
        <legend className="block text-sm font-medium text-gray-700 mb-2">
          Primary markets (check all that apply)
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {MARKET_CHANNELS.map((channel) => (
            <label key={channel} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.primary_markets.includes(channel)}
                onChange={() => toggleMarket(channel)}
                className="h-4 w-4 rounded border-gray-300 text-uif-green focus:ring-uif-green" />
              <span>{channel}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <Field label="What would you like to know?">
        <textarea value={form.message} onChange={update('message')} rows={3}
          placeholder="Questions, what you're hoping AMP could help with, or anything else you want us to know."
          className={inputCls} />
      </Field>

      <div className="pt-2 border-t border-gray-100 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <p className="text-xs text-gray-500">
          We'll respond within a few business days. Your information stays with Up in Farms.
        </p>
        <button type="submit"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-uif-green text-white font-semibold px-6 py-2.5 hover:bg-uif-green-dark transition-colors">
          <Mail className="h-4 w-4 flex-shrink-0" />
          <span className="text-center leading-tight">
            Add me to the<br />workshop list
          </span>
        </button>
      </div>
    </form>
  );
}

const inputCls =
  'w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Hero image (graceful fallback if file isn't present yet)
// ---------------------------------------------------------------------------

function HeroImage() {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <div className="w-full bg-uif-green-dark">
      <img
        src="/amp-hero.jpg"
        alt="Mississippi-grown produce — peppers, tomatoes, onions, broccoli, sweet potatoes, and more"
        onError={() => setFailed(true)}
        className="w-full h-48 sm:h-56 md:h-64 lg:h-72 object-cover"
        loading="lazy"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AmpFunding() {
  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col">
      <header className="border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" aria-label="Up in Farms — home">
            <img src="/logo.jpg" alt="Up in Farms Food Hub" className="h-14 md:h-16 w-auto" />
            <span className="sr-only">Up in Farms</span>
          </a>
          <a href="mailto:support@upinfarms.com"
            className="hidden sm:inline text-sm text-gray-600 hover:text-uif-green">
            support@upinfarms.com
          </a>
        </div>
      </header>

      <main className="flex-1">
        {/* HERO ============================================================ */}
        <section className="bg-gradient-to-br from-uif-green to-uif-green-dark text-white">
          <div className="max-w-5xl mx-auto px-4 pt-16 md:pt-20 pb-6 text-center">
            <p className="uppercase tracking-widest text-xs md:text-sm text-white/80 mb-4">
              USDA-NRCS · Advancing Markets for Producers
            </p>
            <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight">
              Federal funding for small<br />Mississippi farms.
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-2">
              For fruit, vegetable, and livestock producers with a two-year production history.
            </p>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-6">
              Up in Farms LLC is enrolling producers now. AMP is competitive and slots are
              limited — reach out as soon as you can.
            </p>
            <AmpRecruitmentEvents />
          </div>
          <HeroImage />
        </section>

        {/* INTRO =========================================================== */}
        <section className="max-w-3xl mx-auto px-4 py-12 space-y-4">
          {INTRO_PARAGRAPHS.map((p, i) => (
            <p key={i} className="text-lg text-gray-700 leading-relaxed">{p}</p>
          ))}
        </section>

        {/* UMBRELLA TAGLINE — frames the funding streams that follow ======= */}
        <section className="max-w-3xl mx-auto px-4 pb-12">
          <div className="rounded-lg border border-uif-green/30 bg-uif-green/5 p-8 text-center">
            <p className="text-lg md:text-xl text-uif-green-dark leading-relaxed mb-3 italic">
              “{UMBRELLA_TAGLINE}”
            </p>
            <p className="text-2xl md:text-3xl font-bold text-uif-green-dark">
              {UMBRELLA_KICKER}
            </p>
          </div>
        </section>

        {/* COMPETITIVE PROGRAM CALLOUT ===================================== */}
        <section className="max-w-3xl mx-auto px-4 mb-12">
          <div className="rounded-md border-l-4 border-uif-brown bg-uif-brown/5 p-5 flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-uif-brown-dark flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-uif-brown-dark mb-1">A competitive program</div>
              <p className="text-sm text-gray-700 leading-relaxed">
                AMP slots are limited and producers are selected through our enrollment process.
                Smaller fruit and vegetable operations and smaller livestock farms — with at least
                two years of production history — are the program's target audience. The next step
                for interested producers is an AMP Informational Workshop.
              </p>
            </div>
          </div>
        </section>

        {/* FUNDING STREAMS ================================================= */}
        <section className="bg-uif-green/5 border-y border-gray-200 py-14">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-uif-green-dark text-center mb-2">
              What we can help fund
            </h2>
            <p className="text-center text-gray-600 mb-10 italic">
              Three project-specific funding streams, built around the way small farms grow.
            </p>
            <div className="space-y-5">
              {FUNDING_STREAMS.map((s) => (
                <article key={s.number} className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                  <div className="flex items-start gap-4 mb-3">
                    <div className="flex-shrink-0">{s.icon}</div>
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-wider text-uif-green font-semibold mb-1">
                        Funding stream {s.number}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 leading-snug">{s.heading}</h3>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-3">{s.body}</p>
                  {s.examples && (
                    <p className="text-sm text-gray-600 leading-relaxed italic border-l-2 border-uif-green/30 pl-3">
                      {s.examples}
                    </p>
                  )}
                </article>
              ))}
            </div>
            <p className="text-sm text-gray-500 italic mt-6 text-center">
              Specific budgets, per-producer limits, and program caps are governed by the Producer
              Participation Agreement and unpacked in the AMP Informational Workshops.
            </p>
          </div>
        </section>

        {/* WHO AMP IS FOR ================================================== */}
        <section className="max-w-3xl mx-auto px-4 py-14">
          <h2 className="text-3xl font-bold text-uif-green-dark text-center mb-2">
            Who AMP is for
          </h2>
          <p className="text-center text-gray-600 mb-8 italic">
            The program is intentionally sized for smaller operations.
          </p>
          <ul className="space-y-3">
            {AUDIENCE_POINTS.map((p, i) => (
              <li key={i} className="flex items-start gap-3 bg-white rounded-md border border-gray-200 p-4">
                <Users className="h-5 w-5 text-uif-green flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* WHAT WE ASK ===================================================== */}
        <section className="bg-gray-50 border-y border-gray-200 py-14">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-uif-green-dark text-center mb-2">
              What we ask of you
            </h2>
            <p className="text-center text-gray-600 mb-8 italic">
              Manageable. Designed to fit real farm life.
            </p>
            <ul className="space-y-3">
              {COMMITMENTS.map((c, i) => (
                <li key={i} className="flex items-start gap-3 bg-white rounded-md border border-gray-200 p-4">
                  <CheckCircle2 className="h-5 w-5 text-uif-green flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 leading-relaxed">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* HOW IT WORKS ==================================================== */}
        <section className="max-w-3xl mx-auto px-4 py-14">
          <h2 className="text-3xl font-bold text-uif-green-dark text-center mb-3">
            How participation works
          </h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              AMP is structured around your growing seasons — not a three-year lock-in. Each
              season, we sit down with you to lay out the work you intend to do in your funded
              focus areas, help you implement it, verify the work together, and document it in a
              way that satisfies federal recordkeeping rules.
            </p>
            <p>
              Participation incentives are earned along the way — at the planning step, at the
              implementation step, when the work is verified, and when you and our team capture
              what you've learned for the following season. If life requires you to step out for
              a season, you can pick back up when you're ready.
            </p>
            <p>
              The Informational Workshop is where we walk you through this rhythm in detail and
              decide together whether AMP is the right fit for your operation.
            </p>
          </div>
        </section>

        {/* WORKSHOP STATUS BLOCK =========================================== */}
        <section className="max-w-3xl mx-auto px-4 pb-14">
          <div className="rounded-lg border border-uif-green/30 bg-white p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-shrink-0 flex items-center justify-center h-14 w-14 rounded-full bg-uif-green/10">
                <Video className="h-7 w-7 text-uif-green" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-uif-green-dark mb-1">
                  About the AMP Informational Workshop
                </h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  The Informational Workshop is a <strong>one-hour virtual event</strong> designed
                  to accommodate producers from anywhere in the state. We're finalizing dates now
                  and will share scheduling details with everyone on the interest list.
                </p>
                <p className="text-sm text-gray-600 leading-relaxed flex items-start gap-1.5">
                  <Calendar className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Date being finalized. Reach out below and we'll let you know as soon as
                    the next session is scheduled.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* INTEREST FORM =================================================== */}
        <section id="interest" className="bg-uif-green/5 border-t border-gray-200 py-14">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-uif-green-dark text-center mb-2">
              Reach out about AMP
            </h2>
            <p className="text-center text-gray-600 mb-8">
              Send us a note and we'll add you to the next AMP Informational Workshop. No
              commitment — the workshop is your chance to learn the details and decide whether
              AMP fits your farm.
            </p>
            <InterestForm />
          </div>
        </section>

        <section className="text-center py-10">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-uif-green hover:text-uif-green-dark"
          >
            <ArrowLeft className="h-4 w-4" />
            View upcoming workshops
          </a>
        </section>
      </main>

      <footer className="border-t border-gray-200 mt-4">
        <div className="max-w-5xl mx-auto px-4 py-8 text-center text-sm text-gray-500 space-y-2">
          <p>
            Up in Farms LLC · 2093 Lakeland Dr, Jackson, MS 39216 ·{' '}
            <a href="mailto:support@upinfarms.com" className="text-uif-green hover:underline">
              support@upinfarms.com
            </a>
          </p>
          <p className="text-xs text-gray-400">
            AMP — Advancing Markets for Producers — is a USDA-NRCS-funded producer program (the
            successor to USDA's Partnerships for Climate-Smart Commodities). Specific funding
            amounts, eligibility details, and program caps are governed by the Producer
            Participation Agreement and unpacked in the AMP Informational Workshops.
          </p>
        </div>
      </footer>
    </div>
  );
}
